// WP-2.1 (WB-4, S-04 step ⑤, S-42/43): bridge status + the pairing/re-link QR. The QR payload
// is whatever the bridge posted to /bridge/status; it renders as an SVG (qrcode.toString —
// no canvas, jsdom-safe) for the ASSISTANT number's phone to scan (WB-1: dedicated number).
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { useSettings } from '../lib/settings';

const STATUS_LABEL: Record<string, string> = {
  open: 'Connected',
  connecting: 'Waiting for link…',
  close: 'Reconnecting…',
  logged_out: 'Logged out — re-link needed',
};

// WB-4: Baileys rotates the pairing QR roughly every 20-60s and invalidates the previous
// payload — without polling, a mounted panel keeps rendering the QR it fetched at mount and a
// scan attempt on a stale code silently fails. Stop polling once linked (nothing left to await).
const QR_POLL_MS = 3_000;

export function WhatsAppLinkPanel(): React.JSX.Element {
  const { data: settings } = useSettings({
    refetchInterval: (query) => (query.state.data?.bridgeStatus === 'open' ? false : QR_POLL_MS),
  });
  const [qrSvg, setQrSvg] = useState<string | null>(null);

  const status = typeof settings?.bridgeStatus === 'string' ? settings.bridgeStatus : undefined;
  const qrPayload =
    typeof settings?.bridgeQr === 'string' && settings.bridgeQr !== ''
      ? settings.bridgeQr
      : undefined;
  const heartbeat =
    typeof settings?.bridgeLastHeartbeatAt === 'string'
      ? settings.bridgeLastHeartbeatAt
      : undefined;

  useEffect(() => {
    let cancelled = false;
    if (qrPayload === undefined) {
      setQrSvg(null);
      return;
    }
    void QRCode.toString(qrPayload, { type: 'svg', margin: 1 }).then((svg) => {
      if (!cancelled) setQrSvg(svg);
    });
    return () => {
      cancelled = true;
    };
  }, [qrPayload]);

  return (
    <div>
      <p className="mb-1 text-sm text-ink">
        Bridge status:{' '}
        <span
          className={status === 'open' ? 'font-medium text-success' : 'font-medium text-warning'}
        >
          {status !== undefined ? (STATUS_LABEL[status] ?? status) : 'Not running'}
        </span>
      </p>
      {heartbeat !== undefined && (
        <p className="mb-2 text-xs text-muted">
          Last heartbeat {heartbeat.slice(0, 16).replace('T', ' ')}
        </p>
      )}

      {qrSvg !== null ? (
        <div>
          <p className="mb-2 text-xs text-muted">
            On the <strong>assistant number's</strong> phone: WhatsApp → Settings → Linked devices →
            Link a device, then scan:
          </p>
          <div
            data-testid="wa-link-qr"
            className="w-48 rounded-[var(--radius-control)] border border-border bg-white p-2"
            // qrcode's own SVG output — no user content involved
            dangerouslySetInnerHTML={{ __html: qrSvg }}
          />
        </div>
      ) : (
        <p className="text-xs text-muted">
          {status === 'open'
            ? 'Linked. Message the assistant number from your own WhatsApp to start capturing.'
            : 'No pairing code right now — it appears here when the bridge is running and needs a link (WB-4).'}
        </p>
      )}
    </div>
  );
}
